import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import { listRequestedLines, restoreRequestLine, withdrawRequestLine } from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate } from '@/lib/dateFormat';
import type { HardwareRequestDetail, RequestedLine } from '@/types/oscar';

/** The column's width. The API refuses longer rather than cutting the reason. */
const MAX_REASON = 400;

/**
 * What the requester picked - and the approver's way to take off a pick made in
 * error, before the request is decided.
 *
 * <p>Anthony, 28/10/2026: <i>"the IT Asset manager should be able to change the
 * request as the user selected the wrong item - that is remove their requested
 * item. This is done prior to approve."</i></p>
 *
 * ⛔ WITHDRAWN, NOT DELETED. The requester's pick stays as they gave it; a
 * withdrawn one is struck through and says who removed it and why, and can be put
 * back. Stock and the purchase line stop offering it.
 *
 * ⛔ A REASON IS REQUIRED, AND THE SCREEN SAYS WHO READS IT: the requester is told
 * what was removed and why when the request is approved. Said before the approver
 * types, not after.
 *
 * ⛔ NEVER THE LAST ITEM. A request with nothing on it is a decline, and the
 * button is not offered on the last one, with the reason shown.
 *
 * ⚠ A device the APPROVER added has its own "Take off" in the panel below; it is
 * not listed here.
 *
 * ⛔ APPROVERS ONLY, reading included. Renders nothing for anybody else.
 */
export function RequestedLines({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const submissionId = request.SubmissionId;

  const [removing, setRemoving] = useState<RequestedLine | null>(null);
  const [reason, setReason] = useState('');
  const [puttingBack, setPuttingBack] = useState<RequestedLine | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lines = useQuery({
    queryKey: ['hardware-request', submissionId, 'requested-lines'],
    queryFn: ({ signal }) => listRequestedLines(submissionId, signal),
    enabled: canApproveHardware,
  });

  const refresh = () => {
    // The whole request: the availability list and the fulfilment picker read the
    // lines this changes.
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId] });
  };

  const withdraw = useMutation({
    mutationFn: (line: RequestedLine) => withdrawRequestLine(submissionId, line, reason.trim()),
    onSuccess: () => {
      setRemoving(null);
      setReason('');
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not remove the item.'),
  });

  const restore = useMutation({
    mutationFn: (line: RequestedLine) => restoreRequestLine(submissionId, line.WithdrawnLineId!),
    onSuccess: () => {
      setPuttingBack(null);
      setError(null);
      refresh();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not put the item back.'),
  });

  // ⛔ Nothing at all for anybody who cannot decide the request.
  if (!canApproveHardware) return null;

  const data = lines.data;

  if (lines.isError) {
    return (
      <p role="alert" className="mt-3 text-sm text-red">
        Could not read what the requester picked. If this keeps happening, contact Onsite IT Support.
      </p>
    );
  }

  if (!data || data.Lines.length === 0) return null;

  const canWithdraw = data.CanWithdraw;
  const onlyOneLeft = data.LiveLineCount <= 1;

  return (
    <div className="mt-3 border-t border-border pt-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">
        What the requester picked
      </h3>

      <ul className="mt-1 space-y-1">
        {data.Lines.map((l) => {
          const withdrawn = l.WithdrawnLineId !== null;
          return (
            <li
              key={`${l.LineKind}-${l.OptionId ?? l.LineName}`}
              className="flex flex-wrap items-baseline gap-x-3 text-sm text-darkGrey"
            >
              <span className={withdrawn ? 'text-darkGrey/60 line-through' : 'font-semibold text-navy'}>
                {l.LineName}
              </span>
              <span className="text-xs text-darkGrey/60">{l.LineKind}</span>

              {withdrawn && (
                <span className="text-xs text-darkGrey/80">
                  Removed by {l.WithdrawnBy ?? 'an approver'}
                  {l.WithdrawnAtUtc && <> on {formatDate(l.WithdrawnAtUtc)}</>} — {l.WithdrawnReason}
                </span>
              )}

              {canWithdraw && withdrawn && (
                <button
                  type="button"
                  aria-label={`Put ${l.LineName} back on this request`}
                  className="text-xs font-semibold text-navy hover:underline"
                  onClick={() => {
                    setError(null);
                    setPuttingBack(l);
                  }}
                >
                  Put back
                </button>
              )}

              {canWithdraw && !withdrawn && !onlyOneLeft && (
                <button
                  type="button"
                  aria-label={`Remove ${l.LineName} from this request`}
                  className="text-xs font-semibold text-navy hover:underline"
                  onClick={() => {
                    setError(null);
                    setReason('');
                    setRemoving(l);
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* ⚠ SAID, NOT JUST APPLIED. A missing button otherwise reads as a bug. */}
      {!canWithdraw && data.WhyNot && (
        <p className="mt-2 flex items-start gap-2 text-xs text-darkGrey/70">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{data.WhyNot}</span>
        </p>
      )}
      {canWithdraw && onlyOneLeft && (
        <p className="mt-2 flex items-start gap-2 text-xs text-darkGrey/70">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            This is the only item left. To refuse it, decline the request instead, with a reason the
            requester will read.
          </span>
        </p>
      )}

      <AlertDialog
        open={removing !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRemoving(null);
            setError(null);
          }
        }}
        title={`Remove ${removing?.LineName ?? 'this item'}?`}
        description={
          <>
            It stops being offered stock and drops out of the decision and the purchase. The
            requester is told what was removed, and your reason, when you approve the request.
          </>
        }
        confirmLabel={withdraw.isPending ? 'Removing…' : 'Remove'}
        confirmDisabled={reason.trim().length === 0 || withdraw.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          if (removing) withdraw.mutate(removing);
        }}
      >
        <label className="block">
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
            Reason (the requester will read this)
          </span>
          <textarea
            className="w-full rounded border border-border px-2 py-1.5 text-sm"
            rows={3}
            value={reason}
            maxLength={MAX_REASON}
            placeholder="e.g. a monitor was ticked but the request is for a headset"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        {error && removing && (
          <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>

      <AlertDialog
        open={puttingBack !== null}
        onOpenChange={(next) => {
          if (!next) {
            setPuttingBack(null);
            setError(null);
          }
        }}
        title={`Put ${puttingBack?.LineName ?? 'this item'} back?`}
        description={<>It goes back on the request and is offered stock again.</>}
        confirmLabel={restore.isPending ? 'Putting back…' : 'Put it back'}
        confirmDisabled={restore.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          if (puttingBack) restore.mutate(puttingBack);
        }}
      >
        {error && puttingBack && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>
    </div>
  );
}
